import type { Room } from 'livekit-client';

export interface IceCandidateBrief {
  candidateType?: string;
  protocol?: string;
  address?: string;
  port?: number;
  /** relay, host, srflx, prflx when available */
  networkType?: string;
}

export interface IceTransportBrief {
  /** RTT from selected candidate pair (ms), when reported */
  currentRttMs?: number;
  local?: IceCandidateBrief;
  remote?: IceCandidateBrief;
}

type MaybePcHolder = { pc?: RTCPeerConnection };

function collectPeerConnections(room: Room): RTCPeerConnection[] {
  const engine = room.engine as unknown as {
    publisher?: MaybePcHolder;
    subscriber?: MaybePcHolder;
    client?: { publisher?: MaybePcHolder; subscriber?: MaybePcHolder };
  };
  const out: RTCPeerConnection[] = [];
  const add = (pc?: RTCPeerConnection) => {
    if (pc && !out.includes(pc)) out.push(pc);
  };
  add(engine.publisher?.pc);
  add(engine.subscriber?.pc);
  add(engine.client?.publisher?.pc);
  add(engine.client?.subscriber?.pc);
  return out;
}

function pickBriefFromCandidate(entry: RTCStats): IceCandidateBrief {
  const e = entry as unknown as Record<string, unknown>;
  return {
    candidateType: typeof e.candidateType === 'string' ? e.candidateType : undefined,
    protocol: typeof e.protocol === 'string' ? e.protocol : undefined,
    address: typeof e.address === 'string' ? e.address : typeof e.ip === 'string' ? e.ip : undefined,
    port: typeof e.port === 'number' ? e.port : undefined,
    networkType: typeof e.networkType === 'string' ? e.networkType : undefined,
  };
}

/**
 * Best-effort summary of selected ICE candidates + RTT from RTCPeerConnection stats.
 * Helps correlate "rede interna" (host candidates) vs relay.
 */
async function summarizeOnePc(pc: RTCPeerConnection): Promise<IceTransportBrief | undefined> {
  try {
    const report = await pc.getStats();
    let selected: RTCIceCandidatePairStats | null = null;
    report.forEach((entry) => {
      if (entry.type === 'candidate-pair' && 'state' in entry) {
        const pair = entry as RTCIceCandidatePairStats;
        if (pair.state === 'succeeded' || pair.nominated) {
          if (!selected || (pair.state === 'succeeded' && selected.state !== 'succeeded')) {
            selected = pair;
          }
        }
      }
    });
    if (!selected) {
      report.forEach((entry) => {
        if (entry.type === 'candidate-pair') {
          const pair = entry as RTCIceCandidatePairStats;
          if (!selected || (pair.totalRoundTripTime ?? 0) > (selected.totalRoundTripTime ?? 0)) {
            selected = pair;
          }
        }
      });
    }
    if (!selected) return undefined;

    const pair = selected as RTCIceCandidatePairStats;
    const localId = pair.localCandidateId;
    const remoteId = pair.remoteCandidateId;
    let local: IceCandidateBrief | undefined;
    let remote: IceCandidateBrief | undefined;
    if (localId) {
      const le = report.get(localId);
      if (le?.type === 'local-candidate') local = pickBriefFromCandidate(le);
    }
    if (remoteId) {
      const re = report.get(remoteId);
      if (re?.type === 'remote-candidate') remote = pickBriefFromCandidate(re);
    }

    let currentRttMs: number | undefined;
    if (pair.currentRoundTripTime != null && pair.currentRoundTripTime > 0) {
      currentRttMs = Math.round(pair.currentRoundTripTime * 1000);
    } else if (
      pair.totalRoundTripTime != null &&
      pair.responsesReceived != null &&
      pair.responsesReceived > 0
    ) {
      currentRttMs = Math.round(
        (pair.totalRoundTripTime / pair.responsesReceived) * 1000,
      );
    }

    return { currentRttMs, local, remote };
  } catch {
    return undefined;
  }
}

export async function getIceTransportSummaries(room: Room): Promise<{
  publisher?: IceTransportBrief;
  subscriber?: IceTransportBrief;
}> {
  const pcs = collectPeerConnections(room);
  if (pcs.length === 0) return {};

  const summaries = await Promise.all(pcs.map((pc) => summarizeOnePc(pc)));
  const valid = summaries.filter(Boolean) as IceTransportBrief[];
  if (valid.length === 0) return {};

  // Heuristic: first PC is often publisher (outbound), second subscriber — when both exist.
  if (valid.length === 1) {
    return { publisher: valid[0] };
  }
  return { publisher: valid[0], subscriber: valid[1] };
}

/** Mean RTT (ms) across publisher/subscriber transports when both report. */
export function averageIceRttMs(ice: {
  publisher?: IceTransportBrief;
  subscriber?: IceTransportBrief;
}): number | undefined {
  const vals = [ice.publisher?.currentRttMs, ice.subscriber?.currentRttMs].filter(
    (v): v is number => typeof v === 'number' && v > 0,
  );
  if (!vals.length) return undefined;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}
